import React, { useState, useContext } from "react";
import { addQuizForm } from "utils/firebaseFunction";
import { MainContext } from "utils/context";
import QuestionForm from "./QuestionForm/questionForm";
import QuizInfoForm from "./QuizInfoForm/quizInfoForm";
import ReviewQuiz from "./ReviewQuiz/reviewQuiz";
import LoadingSpinner from 'components/LoadingSpinner/LoadingSpinner';

export default function QuizForm() {
  const { userId } = useContext(MainContext);
  const [quizzes, setQuizzes] = useState([]);
  const [quizInfo, setQuizInfo] = useState({
    title: "",
    description: "",
    tab: "",
  });
  const [currentQuestion, setCurrentQuestion] = useState({
    question: "",
    questionType: "multipleChoice",
    options: ["", "", "", ""],
    correctAnswer: 0,
    timeLimit: 60,
    code: "",
    language: "javascript",
    expectedOutput: "",
    fillInBlank: "",
    matchingPairs: [],
    shortAnswer: "",
    imageUrl: "",
    equation: "",
  });
  const [questionNumber, setQuestionNumber] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [step, setStep] = useState(1);

  const addQuestion = () => {
    if (!validateForm()) {
      return;
    }

    setQuizzes([...quizzes, currentQuestion]);

    setCurrentQuestion({
      question: "",
      questionType: "multipleChoice",
      options: ["", "", "", ""],
      correctAnswer: 0,
      timeLimit: 60,
      code: "",
      language: "javascript",
      expectedOutput: "",
      fillInBlank: "",
      matchingPairs: [],
      shortAnswer: "",
      imageUrl: "",
      equation: "",
    });

    setQuestionNumber(questionNumber + 1);
    setError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!userId) {
      setError("User ID is missing. Please log in.");
      return;
    }

    if (quizzes.length === 0) {
      setError("Please add at least one question.");
      return;
    }

    if (!quizInfo.tab) {
      setError("Please select a tab.");
      return;
    }

    setLoading(true);
    setError("");

    const quizData = {
      title: quizInfo.title,
      description: quizInfo.description,
      tab: quizInfo.tab,
      questions: quizzes,
      createdAt: new Date(),
    };

    try {
      const result = await addQuizForm(userId, quizData);
      if (result.success) {
        setError("Quiz submitted successfully!"); // عرض رسالة نجاح
        setQuizzes([]);
        setQuizInfo({ title: "", description: "", tab: "" });
        setQuestionNumber(0); // إعادة تعيين عدد الأسئلة إلى 0
        setStep(1);
      } else {
        setError(result.error);
      }
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleQuestionTypeChange = (e) => {
    const { value } = e.target;
    setCurrentQuestion({
      ...currentQuestion,
      questionType: value,
      options: value === "trueFalse" ? ["True", "False"] : ["", "", "", ""],
      correctAnswer: 0,
      code: "",
      language: "javascript",
      expectedOutput: "",
      fillInBlank: "",
      matchingPairs: [],
      shortAnswer: "",
      imageUrl: "",
      equation: "",
    });
  };

  const handleMatchingPairChange = (index, side, value) => {
    const newPairs = [...currentQuestion.matchingPairs];
    if (!newPairs[index]) {
      newPairs[index] = { left: "", right: "" };
    }
    newPairs[index][side] = value;
    setCurrentQuestion({
      ...currentQuestion,
      matchingPairs: newPairs,
    });
  };

  const addMatchingPair = () => {
    setCurrentQuestion({
      ...currentQuestion,
      matchingPairs: [...currentQuestion.matchingPairs, { left: "", right: "" }],
    });
  };

  const removeMatchingPair = (index) => {
    const newPairs = currentQuestion.matchingPairs.filter((_, i) => i !== index);
    setCurrentQuestion({
      ...currentQuestion,
      matchingPairs: newPairs,
    });
  };

  const validateForm = () => {
    let hasError = false;

    if (!currentQuestion.question.trim()) {
      setError("Please enter the question.");
      hasError = true;
    }

    switch (currentQuestion.questionType) {
      case "multipleChoice":
      case "trueFalse":
        if (currentQuestion.options.some((option) => !option.trim())) {
          setError("Please fill all options.");
          hasError = true;
        }
        if (currentQuestion.correctAnswer === null || currentQuestion.correctAnswer === undefined) {
          setError("Please select the correct answer.");
          hasError = true;
        }
        break;

      case "code":
        if (!currentQuestion.code.trim()) {
          setError("Please provide the code template.");
          hasError = true;
        }
        if (!currentQuestion.expectedOutput.trim()) {
          setError("Please provide the expected output.");
          hasError = true;
        }
        break;

      case "fillInBlank":
        if (!currentQuestion.fillInBlank.trim()) {
          setError("Please provide the fill in the blank template.");
          hasError = true;
        }
        break;

      case "matching":
        if (currentQuestion.matchingPairs.length === 0) {
          setError("Please add at least one matching pair.");
          hasError = true;
        } else if (currentQuestion.matchingPairs.some(pair => !pair.left.trim() || !pair.right.trim())) {
          setError("Please fill all matching pairs.");
          hasError = true;
        }
        break;

      case "shortAnswer":
        if (!currentQuestion.shortAnswer.trim()) {
          setError("Please provide the correct answer.");
          hasError = true;
        }
        break;

      case "image":
        if (!currentQuestion.imageUrl.trim()) {
          setError("Please provide an image URL.");
          hasError = true;
        }
        break;

      case "equation":
        if (!currentQuestion.equation.trim()) {
          setError("Please provide the mathematical equation.");
          hasError = true;
        }
        break;
    }

    if (currentQuestion.timeLimit < 10 || currentQuestion.timeLimit > 200) {
      setError("Time limit must be between 10 and 200 seconds.");
      hasError = true;
    }

    return !hasError;
  };

  return (
    <div className="quizFormContainer">
      <form className="quiz-upload-form" onSubmit={handleSubmit}>
        <h2>Create a New Quiz</h2>

        {loading && <LoadingSpinner size="medium" text="Saving quiz..." />}
        {error && <p className="error-message">{error}</p>}

        {step === 1 && (
          <QuizInfoForm
            quizInfo={quizInfo}
            handleQuizInfoChange={(e) => {
              const { name, value } = e.target;
              setQuizInfo({ ...quizInfo, [name]: value });
            }}
            nextStep={() => setStep(2)}
          />
        )}

        {step === 2 && (
          <QuestionForm
            currentQuestion={currentQuestion}
            handleInputQuestion={(e) => {
              const { name, value } = e.target;
              setCurrentQuestion({ ...currentQuestion, [name]: value });
            }}
            handleOptionChange={(index, value) => {
              const newOptions = [...currentQuestion.options];
              newOptions[index] = value;
              setCurrentQuestion({ ...currentQuestion, options: newOptions });
            }}
            handleCheckOptionChange={(index) => {
              setCurrentQuestion({ ...currentQuestion, correctAnswer: index });
            }}
            handleTimeLimitChange={(e) => {
              setCurrentQuestion({
                ...currentQuestion,
                timeLimit: parseInt(e.target.value, 10),
              });
            }}
            handleQuestionTypeChange={handleQuestionTypeChange}
            handleMatchingPairChange={handleMatchingPairChange}
            addMatchingPair={addMatchingPair}
            removeMatchingPair={removeMatchingPair}
            addQuestion={addQuestion}
            nextStep={() => setStep(3)}
            prevStep={() => setStep(1)}
            questionNumber={questionNumber}
          />
        )}

        {step === 3 && (
          <ReviewQuiz
            quizInfo={quizInfo}
            quizzes={quizzes}
            prevStep={() => setStep(2)}
            handleSubmit={handleSubmit}
          />
        )}
      </form>
    </div>
  );
}